import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { syncUser } from '@/api/api'
import { PageInfo } from '@/api/type'
import { PATHS } from '@/routes/paths'

/** /users/me（ユーザー情報同期）の取得状態 */
export type AuthSyncStatus = 'idle' | 'loading' | 'success' | 'error'

type AppState = {
  accessiblePages: PageInfo[]
  accessibleDomains: string[]
  hasFullAccess: boolean
  isSafeMode: boolean
  features: string[]
  currentUserId: string | null
  showDebugOverlay: boolean
  authSyncStatus: AuthSyncStatus
  setAuthSyncStatus: (status: AuthSyncStatus) => void
  setAccessiblePages: (pages: PageInfo[]) => void
  setAccessibleDomains: (domains: string[]) => void
  setHasFullAccess: (hasFullAccess: boolean) => void
  setIsSafeMode: (isSafeMode: boolean) => void
  setFeatures: (features: string[]) => void
  setCurrentUserId: (id: string | null) => void
  toggleDebugOverlay: () => void
  refreshUserData: () => Promise<void>
  loading: number
  startLoading: () => void
  stopLoading: () => void
}

export const useAppStore = create<AppState>((set) => ({
  accessiblePages: [],
  accessibleDomains: [],
  hasFullAccess: false,
  isSafeMode: false,
  features: [],
  currentUserId: null,
  showDebugOverlay: false,
  authSyncStatus: 'idle',
  setAuthSyncStatus: (status) => set({ authSyncStatus: status }),
  setAccessiblePages: (pages) => set({ accessiblePages: pages }),
  setAccessibleDomains: (domains) => set({ accessibleDomains: domains }),
  setHasFullAccess: (hasFullAccess) => set({ hasFullAccess }),
  setIsSafeMode: (isSafeMode) => set({ isSafeMode }),
  setFeatures: (features) => set({ features }),
  setCurrentUserId: (id) => set({ currentUserId: id }),
  toggleDebugOverlay: () => set((state) => ({ showDebugOverlay: !state.showDebugOverlay })),
  refreshUserData: async () => {
    set({ authSyncStatus: 'loading' })
    try {
      const response = await syncUser()
      if (!response || !response.pages) {
        set({ authSyncStatus: 'error' })
        return
      }
      set({
        accessiblePages: response.pages,
        accessibleDomains: response.content_access?.domains ?? [],
        hasFullAccess: response.content_access?.has_full_access ?? false,
        isSafeMode: response.content_access?.safe_mode ?? false,
        features: response.features ?? [],
        currentUserId: response.id ?? null,
        authSyncStatus: 'success',
      })
    } catch (error) {
      console.error('Failed to refresh user data:', error)
      set({ authSyncStatus: 'error' })
    }
  },
  loading: 0,
  startLoading: () => set((state) => ({ loading: state.loading + 1 })),
  stopLoading: () => set((state) => ({ loading: state.loading - 1 })),
}))

/** TODO を使えるか（管理者か todo 権限。オフライン時もキャッシュしたページ一覧で判定できる） */
export const selectCanUseTodo = (state: AppState) => state.accessiblePages.some((p) => p.path === PATHS.TODO.HOME)

/** メモを使えるか（管理者か memo 権限） */
export const selectCanUseMemo = (state: AppState) => state.accessiblePages.some((p) => p.path === PATHS.MEMO.LIST)

type PersistedAppState = {
  currentIndex: number
  setCurrentIndex: (id: number) => void
  // オフライン用キャッシュ: 最後にオンラインで取得した認証情報
  cachedAccessiblePages: PageInfo[]
  cachedAccessibleDomains: string[]
  cachedHasFullAccess: boolean
  cachedIsSafeMode: boolean
  cachedFeatures: string[]
  cachedCurrentUserId: string | null
  /** 一度でもサインイン済みか（オフライン時のバイパス判定用） */
  hasSignedInBefore: boolean
  cacheAuthData: (data: {
    pages: PageInfo[]
    domains: string[]
    hasFullAccess: boolean
    isSafeMode: boolean
    features: string[]
    userId: string | null
  }) => void
}

export const useAppStorePersisted = create<PersistedAppState>()(
  persist(
    (set) => ({
      currentIndex: 0,
      setCurrentIndex: (id: number) => set({ currentIndex: id }),
      cachedAccessiblePages: [],
      cachedAccessibleDomains: [],
      cachedHasFullAccess: false,
      cachedIsSafeMode: false,
      cachedFeatures: [],
      cachedCurrentUserId: null,
      hasSignedInBefore: false,
      cacheAuthData: (data) =>
        set({
          cachedAccessiblePages: data.pages,
          cachedAccessibleDomains: data.domains,
          cachedHasFullAccess: data.hasFullAccess,
          cachedIsSafeMode: data.isSafeMode,
          cachedFeatures: data.features,
          cachedCurrentUserId: data.userId,
          hasSignedInBefore: true,
        }),
    }),
    {
      name: 'app-state',
    },
  ),
)
